import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import InformationIcon from "../../resources/icons/Information-blue-icon.svg";
import { DownloadIcon } from "lucide-react";
import ErrorIcon from "../../resources/icons/error-live-icon.svg";
import CfLoader from "../../common/CfLoader/CfLoader";
import RefreshIcon from "../../resources/icons/refresh-icon.svg";
import toast from "react-hot-toast";
import Toast from "../../common/Toaster/Toaster";
import CircularCorrectIcon from "../../resources/icons/circular-correct.svg";
import UserIcon from "../../resources/icons/user-icon.svg";
import ImageIcon from "../../resources/icons/image.svg";
import DollarIcon from "../../resources/icons/dollar.svg";
import TimerIcon from "../../resources/icons/timer-icon.svg";
import { POST } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import { downloadPDF } from "../../service/DownloadPDF";
import { TwoPagePdfViewer } from "../../components/TwoPagePdfViewer/TwoPagePdfViewer";
import isPdfFile from "../../utils/isPdfFile";

const Details = () => {
  const { transaction_id } = useParams();
  const navigate = useNavigate();

  const [verificationData, setVerificationData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [uploadedImageLoading, setUploadedImageLoading] = useState(true);

  // State for verification data
  const [isPaymentRequired, setIsPaymentRequired] = useState(false);
  const [verificationLogId, setVerificationLogId] = useState("");
  const [orderId, setOrderId] = useState("");
  const [paymentId, setPaymentId] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [status, setStatus] = useState("");
  const [issuerName, setIssuerName] = useState("");
  const [uploadedDocument, setUploadedDocument] = useState("");
  const [certificateId, setCertificateId] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [signature, setSignature] = useState("");
  const [totalVerificationCost, setTotalVerificationCost] = useState(0);
  const [verificationCost, setVerificationCost] = useState({});
  const [comments, setComments] = useState("");
  const [programmeName, setProgrammeName] = useState("");
  const [svgUrl, setSvgUrl] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [issuerOrganisation, setIssuerOrganisation] = useState("");

  const isPdfUploadedDocument = React.useMemo(
    () => isPdfFile(uploadedDocument),
    [uploadedDocument],
  );

  const stablePdfUrl = React.useMemo(() => {
    if (!isPdfUploadedDocument || !uploadedDocument) return null;
    if (uploadedDocument.startsWith("data:application/pdf")) {
      return uploadedDocument;
    }
    if (uploadedDocument.includes("amazonaws.com")) {
      return uploadedDocument;
    }
    return `data:application/pdf;base64,${uploadedDocument}`;
  }, [uploadedDocument, isPdfUploadedDocument]);

  const fetchVerificationStatus = async (isRefresh = false) => {
    if (!isRefresh) {
      setIsLoading(true);
    }

    try {
      const response = await POST(`${EntityConstants.LEGACY_PAYMENT_STATUS}`, {
        verification_log_id: transaction_id,
      });

      if (response.ok) {
        const data = await response.json();
        const verificationData = data.data;

        // Set all the state from the response
        setIsPaymentRequired(verificationData.is_payment_required);
        setVerificationLogId(verificationData.verification_log_id);
        setOrderId(verificationData.order_id);
        setPaymentId(verificationData.payment_id);
        setPaymentStatus(verificationData.payment_status);
        setStatus(verificationData.status);
        setIssuerName(verificationData.issuer_name);
        setUploadedDocument(verificationData.uploaded_document);
        setCertificateId(verificationData.certificate_id);
        setOrganizationName(verificationData.organization_name);
        setRecipientName(verificationData.recipient_name);
        setRecipientEmail(verificationData.recipient_email);
        setSignature(verificationData.signature);
        setTotalVerificationCost(verificationData.total_verification_cost);
        setVerificationCost(verificationData.verification_cost);
        setComments(verificationData.comments);
        setProgrammeName(verificationData.programme_name);
        setSvgUrl(verificationData.svg_url);
        setPdfUrl(verificationData.pdf_url);
        setVerificationData(verificationData.verification_data || {});
        setIssuerOrganisation(verificationData.issuer_organisation);
      } else {
        console.error("Error fetching verification status");
        toast.error("Failed to fetch verification details");
      }
    } catch (error) {
      console.error("Error fetching verification status:", error);
      toast.error("Failed to fetch verification details");
    } finally {
      setIsLoading(false);
      if (isRefresh) {
        setIsRefreshing(false);
      }
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchVerificationStatus(true);
    Toast({
      type: "success",
      title: "Status Refresh !",
      message: "Your status has been refreshed successfully",
    });
  };

  const formatFieldName = (fieldName) => {
    return fieldName
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .replace(/Id$/i, "ID")
      .replace(/Number$/i, "No.");
  };

  const getDisplayStatus = (status) => {
    switch (status) {
      case "Approved":
        return "Approved";
      case "Approval Pending":
        return "Approval Pending";
      case "Expired":
        return "Expired";
      case "Revoked":
        return "Revoked";
      case "Rejected":
        return "Rejected";
      default:
        return status || "Unknown";
    }
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case "Approved":
        return "Your verification has been completed successfully.";
      case "Approval Pending":
        return "Your request has been submitted successfully and is awaiting approval.";
      case "Expired":
        return "Your verification has expired. Please re-initiate the process.";
      case "Revoked":
        return "Your verification has been revoked.";
      case "Rejected":
        return "Your request has been rejected.";
      default:
        return "Status not available.";
    }
  };

  const shouldShowPaymentDetails = totalVerificationCost || verificationCost;

  useEffect(() => {
    if (transaction_id) {
      fetchVerificationStatus();
    }
  }, [transaction_id]);

  if (isLoading) {
    return (
      <div className="w-full min-h-screen bg-cfSecondary pt-20 pb-10 flex items-center justify-center">
        <CfLoader />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full justify-center items-start bg-white pt-[6rem] pb-[1rem]">
      <div className="w-full bg-white px-4">
        <div className="space-y-4">
          <div>
            <div className="w-full flex flex-col md:flex-row justify-between border border-black rounded-xl p-4">
              <div className="w-full md:w-[50%] flex flex-col gap-4">
                <div className="flex gap-2">
                  <img
                    src={CircularCorrectIcon}
                    height={23}
                    width={23}
                    alt=""
                  />
                  <div className="text-lg font-semibold text-gray-900">
                    Application Status : {""}
                    <span id="details-application-status-value">
                      {getDisplayStatus(status)}
                    </span>
                  </div>
                </div>
                <div className="text-sm text-gray-900 mt-1">
                  {getStatusMessage(status)}
                </div>
              </div>
              {status === "Approval Pending" && (
                <div
                  id="details-refresh-status-button"
                  className="flex gap-2 p-2 border border-black rounded-lg mt-2 md:!mt-0 h-[2.5rem] items-center cursor-pointer w-fit"
                  onClick={handleRefresh}
                >
                  <div>
                    <img
                      src={RefreshIcon}
                      height={25}
                      width={25}
                      alt="Refresh"
                      className={`cursor-pointer transform transition-transform duration-[2000ms] ${
                        isRefreshing ? "rotate-[360deg]" : ""
                      }`}
                    />
                  </div>
                  <div className="text-gray-900 font-medium pr-1">
                    Refresh Status
                  </div>
                </div>
              )}
              {status !== "Approval Pending" && pdfUrl && (
                <button
                  id="details-download-report-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadPDF(
                      pdfUrl,
                      programmeName,
                      verificationLogId,
                      issuerOrganisation
                    );
                  }}
                  className="flex mt-[1rem] sm:mt-0 items-center gap-2 px-3 h-[2.6rem] rounded-[8px] bg-cfPrimaryButton hover:cfPrimaryButtonHover text-white font-semibold shadow-md hover:bg-primary transition-colors duration-200 text-sm sm:text-base"
                >
                  <DownloadIcon className="w-5 h-5 shrink-0" />
                  <span className="hidden sm:inline">
                    Download Verification Report
                  </span>
                  <span className="sm:hidden">Download Report</span>
                </button>
              )}
            </div>
            <div className="w-full flex flex-col md:flex-row justify-between mt-3 gap-3">
              <div className="flex flex-col w-full md:w-[50%] border border-black rounded-xl p-4">
                <div className="flex gap-2">
                  <img src={UserIcon} height={25} width={25} alt="" />
                  <div className="text-lg font-semibold text-gray-900">
                    Candidate Details
                  </div>
                </div>
                {Object.entries(verificationData).map(
                  ([fieldName, fieldValue]) => (
                    <React.Fragment key={fieldName}>
                      <div className="flex justify-between w-full my-3 mt-4">
                        <div className="text-md text-gray-600 font-medium">
                          {formatFieldName(fieldName)}:
                        </div>
                        <div className="truncate text-right justify-end items-end text-md text-gray-900 font-medium">
                          {fieldValue || "Not Available"}
                        </div>
                      </div>
                      <hr />
                    </React.Fragment>
                  )
                )}
                <div className="flex justify-between w-full my-3">
                  <div className=" text-md text-gray-600 font-medium">
                    Comments:{" "}
                  </div>
                  <div className="w-[60%] md:w-[50%] border p-2 rounded-lg border-gray-900 max-h-[6.813rem] overflow-y-auto text-md text-gray-900 font-normal">
                    {comments || "Not Available"}
                  </div>
                </div>
              </div>
              <div className="flex flex-col w-full md:w-[50%] border border-black rounded-xl p-4">
                <div className="flex gap-2">
                  <img src={ImageIcon} height={25} width={25} alt="" />
                  <div className="text-lg font-semibold text-gray-900">
                    Uploaded Document
                  </div>
                </div>
                <div className="w-full mt-3 lg:w-[100%] bg-gray-200 rounded-lg mb-3 lg:mb-0 flex flex-col order-2 lg:order-1">
                  <div className="w-full rounded-lg overflow-hidden">
                    {isPdfUploadedDocument ? (
                      <TwoPagePdfViewer
                        pdfUrl={stablePdfUrl}
                        showZoom={false}
                      />
                    ) : (
                      <div className="w-full h-[400px] flex justify-center items-center">
                        <img
                          src={uploadedDocument}
                          alt="Certificate Preview"
                          className={`object-contain w-full h-full ${
                            uploadedImageLoading ? "hidden" : "block"
                          }`}
                          onLoad={() => setUploadedImageLoading(false)}
                          onError={() => setUploadedImageLoading(false)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {shouldShowPaymentDetails && (
              <div className="w-full flex flex-col border border-black rounded-xl p-4 mt-3">
                <div className="flex gap-2">
                  <img src={DollarIcon} height={25} width={25} alt="" />
                  <div className="text-lg font-semibold text-gray-900">
                    Payment Details
                  </div>
                </div>
                <div className="flex justify-between mb-2 mt-2 text-lg text-gray-900 font-medium">
                  <div className="">Total Amount Paid:</div>
                  <div>₹{totalVerificationCost / 100 || "0"}</div>
                </div>
                <hr />
                <div className="flex justify-between mt-2 text-gray-900">
                  <div>Verification Amount:</div>
                  <div>₹{verificationCost?.verification_fee / 100 || "0"}</div>
                </div>
                <div className="flex justify-between text-gray-900">
                  <div>Platform Fee:</div>
                  <div>₹{verificationCost?.platform_fee / 100 || "0"}</div>
                </div>
                {verificationCost?.igst_percentage &&
                  verificationCost?.igst_fee && (
                    <div className="flex justify-between text-gray-900">
                      <div>IGST ({verificationCost?.igst_percentage}):</div>
                      <div>₹{verificationCost?.igst_fee / 100}</div>
                    </div>
                  )}
                {verificationCost?.cgst_percentage &&
                  verificationCost?.cgst_fee && (
                    <div className="flex justify-between text-gray-900">
                      <div>CGST ({verificationCost?.cgst_percentage}):</div>
                      <div>₹{verificationCost?.cgst_fee / 100}</div>
                    </div>
                  )}
                {verificationCost?.sgst_percentage &&
                  verificationCost?.sgst_fee && (
                    <div className="flex justify-between text-gray-900">
                      <div>SGST ({verificationCost?.sgst_percentage}):</div>
                      <div>₹{verificationCost?.sgst_fee / 100}</div>
                    </div>
                  )}
                {verificationCost?.gst_percentage &&
                  verificationCost?.gst_fee && (
                    <div className="flex justify-between text-gray-900">
                      <div>GST ({verificationCost?.gst_percentage}):</div>
                      <div>₹{verificationCost?.gst_fee / 100}</div>
                    </div>
                  )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Details;
